import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Flame, Building2, Users, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';

const ServicesPage = () => {
  const services = [
    {
      id: 1,
      title: 'Residential Electricity Procurement',
      icon: <Zap size={36} className="text-primary-accent" />,
      description: 'We shop dozens of electricity suppliers in deregulated markets to find you the lowest rates. No more paying inflated utility default rates.',
      forWhom: 'Homeowners and renters in deregulated electricity states like TX, PA, NY, NJ, IL, OH, CT, MA, MD, and NH.',
      whyItMatters: 'Most people don\'t realize they can choose their electricity supplier. We make it easy to switch and save 10-30% on your electric bill with zero hassle.'
    },
    {
      id: 2,
      title: 'Residential Natural Gas Procurement',
      icon: <Flame size={36} className="text-primary-accent" />,
      description: 'We compare natural gas rates from multiple suppliers to lock in the best price for your home heating and cooking needs.',
      forWhom: 'Homeowners in deregulated gas states including TX, PA, NY, NJ, IL, OH, CT, MA, FL, GA, and more.',
      whyItMatters: 'Natural gas prices fluctuate constantly. We help you lock in competitive fixed rates so you\'re protected from price spikes and save money year-round.'
    },
    {
      id: 3,
      title: 'Commercial Electricity Procurement',
      icon: <Building2 size={36} className="text-primary-accent" />,
      description: 'Custom electricity procurement strategies for businesses of all sizes. We leverage your usage volume to negotiate the best rates from competing suppliers.',
      forWhom: 'Small businesses, retail stores, restaurants, offices, warehouses, and commercial properties.',
      whyItMatters: 'Commercial electricity costs add up fast. Our expert negotiation and supplier relationships can deliver significant savings that go straight to your bottom line.'
    },
    {
      id: 4,
      title: 'Commercial Natural Gas Procurement',
      icon: <Flame size={36} className="text-primary-accent" />,
      description: 'Strategic natural gas procurement for commercial and industrial users. We analyze your usage patterns and negotiate custom rates.',
      forWhom: 'Businesses with significant heating needs, restaurants, manufacturing facilities, and multi-site operations.',
      whyItMatters: 'High-volume gas users have more negotiating power. We put that power to work for you, securing rates that smaller buyers can\'t access on their own.'
    },
    {
      id: 5,
      title: 'Multi-Site & Enterprise Solutions',
      icon: <Users size={36} className="text-primary-accent" />,
      description: 'Consolidated energy procurement across multiple locations. One point of contact, one strategy, maximum savings across your entire portfolio.',
      forWhom: 'Franchises, property management companies, retail chains, and organizations with multiple facilities.',
      whyItMatters: 'Managing energy across multiple sites is complex. We simplify it by aggregating your usage for better rates and providing unified reporting and support.'
    },
    {
      id: 6,
      title: 'Ongoing Rate Monitoring',
      icon: <ShieldCheck size={36} className="text-primary-accent" />,
      description: 'We don\'t just set it and forget it. We continuously monitor market rates and alert you when better opportunities arise before your contract expires.',
      forWhom: 'All of our residential and commercial clients.',
      whyItMatters: 'Energy markets change constantly. Our proactive monitoring ensures you\'re always on the best available rate and never miss a savings opportunity.'
    }
  ];

  return (
    <main className="page-content" data-testid="services-page">
      {/* Page Header */}
      <section className="page-header-with-image">
        <div className="page-header-overlay"></div>
        <div className="container page-header-content">
          <span className="overline">What We Offer</span>
          <h1 className="page-title" style={{marginTop: '0.5rem'}}>Energy Procurement Services</h1>
          <p className="page-subtitle">
            We shop the market so you don't have to. Save 10-30% on electricity and natural gas in deregulated markets.
          </p>
        </div>
      </section>

      {/* Awards Banner */}
      <section className="section-dark" style={{padding: '1.5rem 0', borderBottom: '1px solid var(--border-default)'}}>
        <div className="container">
          <div className="awards-banner">
            {[
              { image: '/award-diamond.png', title: 'Diamond Award', subtitle: '2026' },
              { image: '/award-customer-service.png', title: 'Top 10 Service', subtitle: 'Front Runner' },
              { image: '/award-five-star.png', title: 'Five Star Energy', subtitle: 'Brick, Inc' }
            ].map((award, index) => (
              <div key={index} className="award-badge">
                <img src={award.image} alt={award.title} />
                <div className="award-badge-text">
                  <span>{award.title}</span>
                  <span>{award.subtitle}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="section section-light">
        <div className="container">
          <div className="services-list">
            {services.map((service, index) => (
              <div key={service.id}>
                <div className="service-detail" data-testid={`service-detail-${service.id}`}>
                  <div className="service-detail-icon">
                    {service.icon}
                  </div>
                  <div className="service-detail-content">
                    <h2 className="service-detail-title">{service.title}</h2>
                    <div className="service-detail-body">
                      <div className="service-section">
                        <h3 className="service-section-title">What We Do</h3>
                        <p className="service-section-text">{service.description}</p>
                      </div>
                      <div className="service-section">
                        <h3 className="service-section-title">Who It's For</h3>
                        <p className="service-section-text">{service.forWhom}</p>
                      </div>
                      <div className="service-section">
                        <h3 className="service-section-title">Why It Matters</h3>
                        <p className="service-section-text">{service.whyItMatters}</p>
                      </div>
                    </div>
                  </div>
                </div>
                {index < services.length - 1 && (
                  <Separator className="my-12" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-cta-hero">
        <div className="section-cta-overlay"></div>
        <div className="container section-cta-content">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Start Saving?</h2>
            <p className="cta-subtitle">
              It takes just 5 minutes to see how much you could save. Upload your bill or chat with Wattson to get started.
            </p>
            <Link to="/contact">
              <Button className="btn-primary btn-lg" data-testid="services-cta-btn">
                Get Your Free Quote
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
